import { memo } from 'react';
import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import {
  STATUS_BADGE_VARIANT,
  STATUS_TIER,
  statusLabelKey,
  WORKING_ACTIVE_LABEL_KEY,
} from '../constants';
import { CHIP_BASE, CHIP_LABEL } from '../utils';

/**
 * Nudge toward the measured prototype pill via className only (we don't touch
 * @aimsos/ui). Only letter-spacing can be adapted this way on the default surface:
 * the Badge's own `text-[11px]`/`font-medium` win over any font-size/weight passed
 * here. Color stays on the Badge variant so status color stays single-sourced.
 */
const PILL_TYPOGRAPHY = 'tracking-[0.1px]';

/**
 * Provider Detail eyebrow (prototype `.mkt-card-status`): soft tint + border +
 * glowing dot — overrides Badge fill via `!` utilities so we never fork @aimsos/ui.
 */
const DETAIL_BASE = `${CHIP_BASE} !gap-1 !tracking-[0.04em]`;

const DETAIL_TONE = {
  working:
    '!border-emerald-500/30 !bg-emerald-500/10 !text-emerald-400 dark:!border-emerald-500/30 dark:!bg-emerald-500/10 dark:!text-[#34d39c]',
  amber:
    '!border-amber-500/30 !bg-amber-500/10 !text-amber-400 dark:!border-amber-500/30 dark:!bg-amber-500/10 dark:!text-[#fbbf24]',
  red: '!border-red-500/30 !bg-red-500/10 !text-red-400 dark:!border-red-500/30 dark:!bg-red-500/10 dark:!text-red-400',
  neutral:
    '!border-slate-400/20 !bg-slate-400/[0.08] !text-content-faint dark:!border-slate-400/20 dark:!bg-slate-400/[0.08]',
};

function detailToneFor(status) {
  if (status === 'working') return DETAIL_TONE.working;
  if (STATUS_TIER[status] === 'amber') return DETAIL_TONE.amber;
  if (STATUS_TIER[status] === 'red') return DETAIL_TONE.red;
  return DETAIL_TONE.neutral;
}

/**
 * The single shared status pill. Renders a provider/instance `status` from the
 * Status System tokens — no status color/label logic lives anywhere else.
 *
 * - `not_connected` renders nothing (the header shows a Connect CTA instead).
 * - Unknown/missing status falls back to the `unknown` (neutral) pill.
 * - `working` shows "Ready to use" by default, or "Active" when `activeVariant`
 *   is set (recent activity — the caller decides the recency threshold).
 * - `withDot` renders the leading status dot (matches the prototype: a 6px
 *   `currentColor` circle before the label). The dot inherits the variant text
 *   color, so it stays driven by status tokens — no extra color logic.
 * - `surface="detail"` softens the Badge to the Provider Detail eyebrow chip.
 *
 * Radius is `default` (8px) to match the measured prototype pill (rounded rect,
 * not a full pill), per the design's exact treatment on the Integrations home.
 */
function StatusPill({
  status,
  size = 'small',
  withLabel = true,
  withDot = true,
  activeVariant = false,
  surface = 'default',
  className = '',
}) {
  const { t } = useTranslation('admin-studio');

  if (status === 'not_connected') return null;

  // Normalize any unrecognized/missing status to `unknown` so both the variant
  // AND the label fall back together — otherwise a stray status string would
  // build `integrations.status.<raw>` and render a raw i18n key instead of the
  // documented neutral "Unknown" pill.
  const safeStatus = status && status in STATUS_BADGE_VARIANT ? status : 'unknown';
  const variant = STATUS_BADGE_VARIANT[safeStatus];
  const labelKey =
    safeStatus === 'working' && activeVariant ? WORKING_ACTIVE_LABEL_KEY : statusLabelKey(safeStatus);

  const isDetail = surface === 'detail';
  const surfaceClass = isDetail ? [DETAIL_BASE, detailToneFor(safeStatus)].join(' ') : PILL_TYPOGRAPHY;

  return (
    <Badge
      variant={variant}
      size={size}
      radius="default"
      className={[surfaceClass, className].filter(Boolean).join(' ')}
    >
      {withDot ? (
        <span
          aria-hidden="true"
          className={[
            'inline-block shrink-0 rounded-full bg-current',
            isDetail
              ? 'h-1.5 w-1.5 shadow-[0_0_0_2px_color-mix(in_srgb,currentColor_18%,transparent)]'
              : 'h-1.5 w-1.5',
          ].join(' ')}
        />
      ) : null}
      {withLabel ? <span className={CHIP_LABEL}>{t(labelKey)}</span> : null}
    </Badge>
  );
}

export default memo(StatusPill);
